import { del, downloadFile, get, patch, post, put } from "@/lib/api-client-v2";
import type {
  EehContract,
  EehContractCreate,
  EehContractUpdate,
  EehMeasure,
  EehMeasureCreate,
  EehMeasureUpdate,
  EehMeasureYear,
  EehMeta,
} from "@/types/eeh";

function base(objectId: number): string {
  return `/objects/${objectId}/eeh`;
}

export const eehApi = {
  getMeta(objectId: number): Promise<EehMeta> {
    return get<EehMeta>(`${base(objectId)}/meta`);
  },

  listContracts(objectId: number): Promise<EehContract[]> {
    return get<EehContract[]>(`${base(objectId)}/contracts`);
  },

  createContract(objectId: number, data: EehContractCreate): Promise<EehContract> {
    return post<EehContract>(`${base(objectId)}/contracts`, data);
  },

  updateContract(
    objectId: number,
    contractId: number,
    data: EehContractUpdate,
  ): Promise<EehContract> {
    return patch<EehContract>(`${base(objectId)}/contracts/${contractId}`, data);
  },

  deleteContract(objectId: number, contractId: number): Promise<void> {
    return del<void>(`${base(objectId)}/contracts/${contractId}`);
  },

  listMeasures(
    objectId: number,
    params?: {
      contract_id?: number;
      geometry_id?: number;
      current_only?: boolean;
    },
  ): Promise<EehMeasure[]> {
    const q = new URLSearchParams();
    if (params?.contract_id != null) q.set("contract_id", String(params.contract_id));
    if (params?.geometry_id != null) q.set("geometry_id", String(params.geometry_id));
    if (params?.current_only) q.set("current_only", "true");
    const qs = q.toString();
    return get<EehMeasure[]>(`${base(objectId)}/measures${qs ? `?${qs}` : ""}`);
  },

  createMeasure(objectId: number, data: EehMeasureCreate): Promise<EehMeasure> {
    return post<EehMeasure>(`${base(objectId)}/measures`, data);
  },

  updateMeasure(
    objectId: number,
    measureId: number,
    data: EehMeasureUpdate,
  ): Promise<EehMeasure> {
    return patch<EehMeasure>(`${base(objectId)}/measures/${measureId}`, data);
  },

  deleteMeasure(objectId: number, measureId: number): Promise<void> {
    return del<void>(`${base(objectId)}/measures/${measureId}`);
  },

  replaceYears(
    objectId: number,
    measureId: number,
    years: EehMeasureYear[],
  ): Promise<EehMeasure> {
    return put<EehMeasure>(`${base(objectId)}/measures/${measureId}/years`, { years });
  },

  async downloadReport(
    objectId: number,
    form: "pr2" | "pr6",
    params?: { year_from?: number; year_to?: number },
  ): Promise<void> {
    const blob = await downloadFile(`${base(objectId)}/reports/${form}`, {
      ...(params?.year_from != null ? { year_from: params.year_from } : {}),
      ...(params?.year_to != null ? { year_to: params.year_to } : {}),
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${form.toUpperCase()}-eeh-object-${objectId}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  },
};
